import posthog from 'posthog-js';
import * as amplitude from '@amplitude/unified';
import mixpanel from 'mixpanel-browser';
import { workspaceService } from '../../services/api/workspace.service';

import {
  type AccionBorradorAnalitica,
  EVENTOS_ANALITICA,
  type EntidadImportacion,
  type EntornoAnalitica,
  type ErroresRangoImportacion,
  type EstadoComprobanteAnalitica,
  type FormaPagoAnalitica,
  type MotivoCierreSesionAnalitica,
  type MotivoAbandonoVenta,
  type OrigenAyudaAnalitica,
  type OrigenCliente,
  type OrigenProducto,
  type OrigenVenta,
  type ResultadoImportacion,
  type TipoAyudaAnalitica,
  type TipoComprobanteAnalitica,
} from './eventosAnalitica';
import {
  resolverContextoEmpresaAnalitica,
  type ContextoIdentidadAnalitica,
} from './identidadAnalitica';
import {
  esEntornoAnaliticaHabilitado,
  obtenerConfiguracionAnaliticaPublica,
} from './configuracionAnalitica';
import type { UnifiedOptions } from '@amplitude/unified';

type PropiedadesAnalitica = Record<string, unknown>;
const { amplitudeApiKey, amplitudeReplaySampleRate, mixpanelToken, posthogKey } = obtenerConfiguracionAnaliticaPublica();

type PosthogConEstado = typeof posthog & {
  __loaded?: boolean;
  resetGroups?: () => void;
};

type MixpanelConSuperProps = typeof mixpanel & {
  register?: (properties: Record<string, unknown>) => void;
  unregister?: (propertyName: string) => void;
};

const esNavegador = (): boolean => typeof window !== 'undefined';

let contextoIdentidadActual: ContextoIdentidadAnalitica | null = null;
let firmaContextoIdentidadActual: string | null = null;

const posthogDisponible = (): boolean => {
  const cliente = posthog as PosthogConEstado;
  return cliente.__loaded === true;
};

const ejecutarOperacionAnaliticaSegura = (operacion: () => void): void => {
  try {
    operacion();
  } catch {
    // silencioso
  }
};

const obtenerContextoEmpresaTentativo = () => {
  const empresaId = workspaceService.getCurrentEmpresaId() ?? undefined;
  const empresa = workspaceService.getCurrentEmpresa();
  return resolverContextoEmpresaAnalitica(empresaId, empresa);
};

const resolverEntornoAnaliticaContextual = (entorno?: EntornoAnalitica): EntornoAnalitica => {
  return entorno ?? contextoIdentidadActual?.entorno ?? 'demo';
};

const normalizarFormaPagoAnalitica = (valor?: string): FormaPagoAnalitica | undefined => {
  if (!valor) return undefined;
  const normalizado = valor.trim().toLowerCase();
  if (normalizado.includes('cred')) return 'credito';
  if (normalizado.includes('cont')) return 'contado';
  return undefined;
};

const normalizarTipoComprobanteAnalitica = (valor?: string): TipoComprobanteAnalitica | undefined => {
  if (!valor) return undefined;
  const normalizado = valor.trim().toLowerCase();
  if (normalizado.includes('fact')) return 'factura';
  if (normalizado.includes('bolet')) return 'boleta';
  if (normalizado.includes('credito')) return 'nota_credito';
  if (normalizado.includes('débito') || normalizado.includes('debito')) return 'nota_debito';
  return undefined;
};

const puedeCapturarEventos = (): boolean => {
  if (!esNavegador()) return false;
  if (!esEntornoAnaliticaHabilitado()) return false;
  if (!posthogKey) return false;
  return posthogDisponible();
};

const amplitudeDisponible = (): boolean => {
  if (!esNavegador()) return false;
  if (!esEntornoAnaliticaHabilitado()) return false;
  return Boolean(amplitudeApiKey);
};

const mixpanelDisponible = (): boolean => {
  if (!esNavegador()) return false;
  if (!esEntornoAnaliticaHabilitado()) return false;
  return Boolean(mixpanelToken);
};

let mixpanelInicializado = false;
let mixpanelInicializacionBloqueada = false;
const asegurarMixpanelInicializado = (): boolean => {
  if (mixpanelInicializacionBloqueada) return false;
  if (!mixpanelDisponible() || mixpanelInicializado) return mixpanelInicializado;

  try {
    mixpanel.init(mixpanelToken as string, {
      track_pageview: false,
      persistence: 'localStorage',
      ignore_dnt: true,
    });
    mixpanelInicializado = true;
  } catch {
    mixpanelInicializacionBloqueada = true;
  }

  return mixpanelInicializado;
};

let amplitudeInicializado = false;
let amplitudeInicializacionBloqueada = false;
const asegurarAmplitudeInicializado = (): boolean => {
  if (amplitudeInicializacionBloqueada) return false;
  if (amplitudeInicializado) return true;
  if (!amplitudeDisponible()) return false;

  try {
    const opciones: UnifiedOptions = {
      analytics: { defaultTracking: false },
      ...(amplitudeReplaySampleRate > 0
        ? { sessionReplay: { sampleRate: amplitudeReplaySampleRate } }
        : {}),
    };
    void amplitude.initAll(amplitudeApiKey, opciones);
    amplitudeInicializado = true;
  } catch {
    amplitudeInicializacionBloqueada = true;
  }

  return amplitudeInicializado;
};

const construirPropiedadesBase = (propiedades?: PropiedadesAnalitica): PropiedadesAnalitica => {
  const propiedadesBase: PropiedadesAnalitica = {
    ruta_actual: esNavegador() ? window.location.pathname : '',
    timestamp_cliente: new Date().toISOString(),
  };

  if (contextoIdentidadActual?.companyId) {
    propiedadesBase.company_id = contextoIdentidadActual.companyId;
  }

  if (contextoIdentidadActual?.companyRuc) {
    propiedadesBase.company_ruc = contextoIdentidadActual.companyRuc;
  }

  if (contextoIdentidadActual?.companyName) {
    propiedadesBase.company_name = contextoIdentidadActual.companyName;
  }

  if (typeof contextoIdentidadActual?.companyConfigured === 'boolean') {
    propiedadesBase.company_configured = contextoIdentidadActual.companyConfigured;
  }

  if (contextoIdentidadActual?.establecimientoId) {
    propiedadesBase.establecimiento_id = contextoIdentidadActual.establecimientoId;
  }

  if (contextoIdentidadActual?.userRole) {
    propiedadesBase.user_role = contextoIdentidadActual.userRole;
  }

  if (contextoIdentidadActual?.userEmail) {
    propiedadesBase.user_email = contextoIdentidadActual.userEmail;
  }

  if (contextoIdentidadActual?.userName) {
    propiedadesBase.user_name = contextoIdentidadActual.userName;
  }

  if (contextoIdentidadActual?.entorno) {
    propiedadesBase.entorno = contextoIdentidadActual.entorno;
  }

  if (!propiedades) return propiedadesBase;

  return { ...propiedadesBase, ...propiedades };
};

const construirFirmaContexto = (contexto: ContextoIdentidadAnalitica | null): string | null => {
  if (!contexto) return null;
  return JSON.stringify(contexto);
};

const construirPropiedadesUsuario = (contexto: ContextoIdentidadAnalitica): Record<string, unknown> => ({
  user_email: contexto.userEmail,
  user_name: contexto.userName,
  user_role: contexto.userRole,
  user_status: contexto.userStatus,
  entorno: contexto.entorno,
  company_configured: contexto.companyConfigured,
  company_id: contexto.companyId,
  company_ruc: contexto.companyRuc,
  company_name: contexto.companyName,
  establecimiento_id: contexto.establecimientoId,
});

const limpiarValoresIndefinidos = (propiedades: Record<string, unknown>): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(propiedades).filter(([, valor]) => valor !== undefined),
  );
};

function establecerContextoAnalitico(contexto: ContextoIdentidadAnalitica | null): void {
  contextoIdentidadActual = contexto;
}

export function obtenerContextoAnaliticoActual(): ContextoIdentidadAnalitica | null {
  return contextoIdentidadActual;
}

export function obtenerEntornoAnaliticoResuelto(entorno?: EntornoAnalitica): EntornoAnalitica {
  return resolverEntornoAnaliticaContextual(entorno);
}

export function resetearIdentidadAnalitica(): void {
  establecerContextoAnalitico(null);

  const clientePosthog = posthog as PosthogConEstado;
  if (posthogDisponible()) {
    ejecutarOperacionAnaliticaSegura(() => {
      clientePosthog.resetGroups?.();
      posthog.reset();
    });
  }

  if (amplitudeDisponible() && asegurarAmplitudeInicializado()) {
    ejecutarOperacionAnaliticaSegura(() => {
      amplitude.reset();
    });
  }

  if (mixpanelDisponible() && asegurarMixpanelInicializado()) {
    ejecutarOperacionAnaliticaSegura(() => {
      mixpanel.reset();
    });
  }

  firmaContextoIdentidadActual = null;
}

export function sincronizarIdentidadAnalitica(contexto: ContextoIdentidadAnalitica | null): void {
  const nuevaFirma = construirFirmaContexto(contexto);
  if (nuevaFirma === firmaContextoIdentidadActual) return;

  firmaContextoIdentidadActual = nuevaFirma;
  establecerContextoAnalitico(contexto);

  if (!contexto) {
    resetearIdentidadAnalitica();
    return;
  }

  const propiedadesUsuario = limpiarValoresIndefinidos(construirPropiedadesUsuario(contexto));

  if (puedeCapturarEventos()) {
    ejecutarOperacionAnaliticaSegura(() => {
      posthog.identify(contexto.userId, propiedadesUsuario);
    });

    if (contexto.companyId) {
      ejecutarOperacionAnaliticaSegura(() => {
        posthog.group('company', contexto.companyId!, limpiarValoresIndefinidos({
          company_id: contexto.companyId,
          entorno: contexto.entorno,
          company_configured: contexto.companyConfigured,
        }));
      });
    } else {
      ejecutarOperacionAnaliticaSegura(() => {
        (posthog as PosthogConEstado).resetGroups?.();
      });
    }
  }

  if (amplitudeDisponible() && asegurarAmplitudeInicializado()) {
    ejecutarOperacionAnaliticaSegura(() => {
      amplitude.setUserId(contexto.userId);

      const identify = new amplitude.Identify();
      identify.unset('company_name');
      identify.unset('entorno_emision');
      identify.unset('entorno_sunat');
      Object.entries(propiedadesUsuario).forEach(([key, value]) => {
        if (value !== undefined) {
          identify.set(key, value as string | number | boolean);
        }
      });
      amplitude.identify(identify);

      if (contexto.companyId) {
        amplitude.setGroup('company', contexto.companyId);
      }
    });
  }

  if (mixpanelDisponible() && asegurarMixpanelInicializado()) {
    ejecutarOperacionAnaliticaSegura(() => {
      mixpanel.identify(contexto.userId);

      const mixpanelClient = mixpanel as MixpanelConSuperProps;
      mixpanelClient.unregister?.('company_name');
      mixpanelClient.unregister?.('entorno_emision');
      mixpanelClient.unregister?.('entorno_sunat');
      mixpanelClient.register?.(propiedadesUsuario);

      if (!contexto.companyId) mixpanelClient.unregister?.('company_id');
      if (!contexto.establecimientoId) mixpanelClient.unregister?.('establecimiento_id');
    });
  }
}

function capturarEvento(nombreEvento: string, propiedades?: PropiedadesAnalitica): void {
  const posthogHabilitado = puedeCapturarEventos();
  const amplitudeHabilitado = amplitudeDisponible() && asegurarAmplitudeInicializado();
  const mixpanelHabilitado = mixpanelDisponible();

  if (!posthogHabilitado && !amplitudeHabilitado && !mixpanelHabilitado) return;

  const propiedadesEvento = construirPropiedadesBase(propiedades);

  if (posthogHabilitado) {
    ejecutarOperacionAnaliticaSegura(() => posthog.capture(nombreEvento, propiedadesEvento));
  }

  if (amplitudeHabilitado) {
    ejecutarOperacionAnaliticaSegura(() => amplitude.track(nombreEvento, propiedadesEvento));
  }

  if (mixpanelHabilitado && asegurarMixpanelInicializado()) {
    ejecutarOperacionAnaliticaSegura(() => mixpanel.track(nombreEvento, propiedadesEvento));
  }
}

function construirPropiedadesRegistroUsuarioCompletado(
  entrada?: { entorno?: EntornoAnalitica },
): PropiedadesAnalitica {
  const entorno = resolverEntornoAnaliticaContextual(entrada?.entorno);

  if (contextoIdentidadActual?.companyId) return { entorno };

  const contextoEmpresa = obtenerContextoEmpresaTentativo();

  if (!contextoEmpresa?.companyId) return { entorno };

  return limpiarValoresIndefinidos({ ...contextoEmpresa, entorno });
}

export function registrarRegistroUsuarioCompletado(entrada?: {
  entorno?: EntornoAnalitica;
  userEmail?: string;
  userName?: string;
}): void {
  capturarEvento(
    EVENTOS_ANALITICA.REGISTRO_USUARIO_COMPLETADO,
    {
      ...construirPropiedadesRegistroUsuarioCompletado(entrada),
      ...(entrada?.userEmail?.trim() ? { user_email_registro: entrada.userEmail.trim() } : {}),
      ...(entrada?.userName?.trim() ? { user_name_registro: entrada.userName.trim() } : {}),
    },
  );
}

export function registrarRegistroEmpresaExitoso(entrada?: {
  ruc?: string;
  razonSocial?: string;
  nombreComercial?: string;
}): void {
  const nombreEmpresa = entrada?.razonSocial?.trim() || entrada?.nombreComercial?.trim() || undefined;
  capturarEvento(EVENTOS_ANALITICA.REGISTRO_EMPRESA_EXITOSO, {
    entorno: resolverEntornoAnaliticaContextual(),
    origen: 'formulario_empresa',
    ...(entrada?.ruc?.trim() ? { company_ruc: entrada.ruc.trim() } : {}),
    ...(nombreEmpresa ? { company_name: nombreEmpresa } : {}),
  });
}

export function registrarInicioSesionExitoso(entrada?: {
  userId?: string;
  userEmail?: string;
  userName?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.INICIO_SESION_EXITOSO, {
    origen: 'formulario_login',
    ...(entrada?.userId ? { user_id_login: entrada.userId } : {}),
    ...(entrada?.userEmail?.trim() ? { user_email_login: entrada.userEmail.trim() } : {}),
    ...(entrada?.userName?.trim() ? { user_name_login: entrada.userName.trim() } : {}),
  });
}

export function registrarCierreSesionExitoso(entrada?: {
  motivo?: MotivoCierreSesionAnalitica;
  origen?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.CIERRE_SESION_EXITOSO, {
    motivo_cierre: entrada?.motivo ?? 'manual',
    origen_cierre: entrada?.origen ?? 'ui',
  });
}

export function registrarCajaAbiertaExitoso(entrada?: {
  montoInicialTotal?: number;
  montoInicialPen?: number;
  montoInicialUsd?: number;
  cajaId?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.CAJA_ABIERTA_EXITOSO, {
    origen: 'caja',
    ...(typeof entrada?.montoInicialTotal === 'number' ? { monto_inicial_total: entrada.montoInicialTotal } : {}),
    ...(typeof entrada?.montoInicialPen === 'number' ? { monto_inicial_pen: entrada.montoInicialPen } : {}),
    ...(typeof entrada?.montoInicialUsd === 'number' ? { monto_inicial_usd: entrada.montoInicialUsd } : {}),
    ...(entrada?.cajaId ? { caja_id: entrada.cajaId } : {}),
  });
}

export function registrarMovimientoCajaRegistrado(entrada?: {
  tipoMovimiento?: 'ingreso' | 'egreso' | 'otro';
  monto?: number;
  moneda?: string;
  medioPago?: string;
  concepto?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.MOVIMIENTO_CAJA_REGISTRADO, {
    origen: 'caja',
    ...(entrada?.tipoMovimiento ? { tipo_movimiento: entrada.tipoMovimiento } : {}),
    ...(typeof entrada?.monto === 'number' ? { monto_movimiento: entrada.monto } : {}),
    ...(entrada?.moneda ? { moneda_movimiento: entrada.moneda } : {}),
    ...(entrada?.medioPago ? { medio_pago_movimiento: entrada.medioPago } : {}),
    ...(entrada?.concepto ? { concepto_movimiento: entrada.concepto } : {}),
  });
}

export function registrarCajaCerradaExitoso(entrada?: {
  montoFinalTotal?: number;
  montoFinalPen?: number;
  montoFinalUsd?: number;
  descuadrePen?: number;
  descuadreUsd?: number;
}): void {
  capturarEvento(EVENTOS_ANALITICA.CAJA_CERRADA_EXITOSO, {
    origen: 'caja',
    ...(typeof entrada?.montoFinalTotal === 'number' ? { monto_final_total: entrada.montoFinalTotal } : {}),
    ...(typeof entrada?.montoFinalPen === 'number' ? { monto_final_pen: entrada.montoFinalPen } : {}),
    ...(typeof entrada?.montoFinalUsd === 'number' ? { monto_final_usd: entrada.montoFinalUsd } : {}),
    ...(typeof entrada?.descuadrePen === 'number' ? { descuadre_pen: entrada.descuadrePen } : {}),
    ...(typeof entrada?.descuadreUsd === 'number' ? { descuadre_usd: entrada.descuadreUsd } : {}),
  });
}

export function registrarFlujoVentaAbandonado(entrada: {
  origenVenta: OrigenVenta;
  motivoAbandono?: MotivoAbandonoVenta;
  etapa?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.FLUJO_VENTA_ABANDONADO, {
    origen_venta: entrada.origenVenta,
    ...(entrada.motivoAbandono ? { motivo_abandono: entrada.motivoAbandono } : {}),
    ...(entrada.etapa ? { etapa_abandono: entrada.etapa } : {}),
  });
}

export function registrarComprobanteEstadoActualizado(entrada: {
  estado: EstadoComprobanteAnalitica;
  tipoComprobante?: TipoComprobanteAnalitica;
  tipoComprobanteRaw?: string;
  formaPago?: FormaPagoAnalitica;
  formaPagoRaw?: string;
  origenVenta?: OrigenVenta;
  serie?: string;
  correlativo?: string;
}): void {
  const tipoNormalizado = entrada.tipoComprobante ?? normalizarTipoComprobanteAnalitica(entrada.tipoComprobanteRaw);
  const formaPagoNormalizada = entrada.formaPago ?? normalizarFormaPagoAnalitica(entrada.formaPagoRaw);
  capturarEvento(EVENTOS_ANALITICA.COMPROBANTE_ESTADO_ACTUALIZADO, {
    estado: entrada.estado,
    ...(tipoNormalizado ? { tipo_comprobante: tipoNormalizado } : {}),
    ...(formaPagoNormalizada ? { forma_pago: formaPagoNormalizada } : {}),
    ...(entrada.origenVenta ? { origen_venta: entrada.origenVenta } : {}),
    ...(entrada.serie ? { serie_comprobante: entrada.serie } : {}),
    ...(entrada.correlativo ? { correlativo_comprobante: entrada.correlativo } : {}),
  });
}

export function registrarAyudaConsultada(entrada: {
  tipoAyuda: TipoAyudaAnalitica;
  origen: OrigenAyudaAnalitica;
  modulo?: string;
  tourId?: string;
  recurso?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.AYUDA_CONSULTADA, {
    tipo_ayuda: entrada.tipoAyuda,
    origen: entrada.origen,
    ...(entrada.modulo ? { modulo_ayuda: entrada.modulo } : {}),
    ...(entrada.tourId ? { tour_id: entrada.tourId } : {}),
    ...(entrada.recurso ? { recurso_ayuda: entrada.recurso } : {}),
  });
}

export function registrarBorradorAccionRealizada(entrada: {
  accion: AccionBorradorAnalitica;
  origenVenta?: OrigenVenta;
  modulo?: string;
  tipoDocumento?: string;
  serie?: string;
}): void {
  const tipoComprobanteNormalizado = normalizarTipoComprobanteAnalitica(entrada.tipoDocumento);
  capturarEvento(EVENTOS_ANALITICA.BORRADOR_ACCION_REALIZADA, {
    accion: entrada.accion,
    ...(entrada.origenVenta ? { origen_venta: entrada.origenVenta } : {}),
    ...(entrada.modulo ? { modulo: entrada.modulo } : {}),
    ...(tipoComprobanteNormalizado ? { tipo_documento: tipoComprobanteNormalizado } : {}),
    ...(entrada.serie ? { serie_documento: entrada.serie } : {}),
  });
}

export function registrarVentaCompletada(entrada: {
  entorno: EntornoAnalitica;
  origenVenta: OrigenVenta;
  formaPago?: FormaPagoAnalitica;
  formaPagoRaw?: string;
  tipoComprobanteRaw?: string;
  montoTotal?: number;
}): void {
  const formaPagoNormalizada = entrada.formaPago ?? normalizarFormaPagoAnalitica(entrada.formaPagoRaw);
  const tipoComprobante = normalizarTipoComprobanteAnalitica(entrada.tipoComprobanteRaw);
  capturarEvento(EVENTOS_ANALITICA.VENTA_COMPLETADA, {
    entorno: entrada.entorno,
    origen_venta: entrada.origenVenta,
    ...(formaPagoNormalizada ? { forma_pago: formaPagoNormalizada } : {}),
    ...(tipoComprobante ? { tipo_comprobante: tipoComprobante } : {}),
    ...(typeof entrada.montoTotal === 'number' ? { monto_total: entrada.montoTotal } : {}),
  });
}

export function registrarPrimeraVentaCompletada(entrada: {
  entorno: EntornoAnalitica;
  origenVenta: OrigenVenta;
  formaPago?: FormaPagoAnalitica;
  formaPagoRaw?: string;
  tipoComprobanteRaw?: string;
  montoTotal?: number;
}): void {
  const formaPagoNormalizada = entrada.formaPago ?? normalizarFormaPagoAnalitica(entrada.formaPagoRaw);
  const tipoComprobante = normalizarTipoComprobanteAnalitica(entrada.tipoComprobanteRaw);
  capturarEvento(EVENTOS_ANALITICA.PRIMERA_VENTA_COMPLETADA, {
    entorno: entrada.entorno,
    origen_venta: entrada.origenVenta,
    ...(formaPagoNormalizada ? { forma_pago: formaPagoNormalizada } : {}),
    ...(tipoComprobante ? { tipo_comprobante: tipoComprobante } : {}),
    ...(typeof entrada.montoTotal === 'number' ? { monto_total: entrada.montoTotal } : {}),
  });
}

export function registrarProductoCreadoExitoso(entrada: {
  entorno: EntornoAnalitica;
  origen: OrigenProducto;
  codigo?: string;
  nombre?: string;
  categoria?: string;
  tipoProducto?: string;
}): void {
  capturarEvento(EVENTOS_ANALITICA.PRODUCTO_CREADO_EXITOSO, {
    entorno: entrada.entorno,
    origen: entrada.origen,
    ...(entrada.codigo ? { producto_codigo: entrada.codigo } : {}),
    ...(entrada.nombre ? { producto_nombre: entrada.nombre } : {}),
    ...(entrada.categoria ? { producto_categoria: entrada.categoria } : {}),
    ...(entrada.tipoProducto ? { producto_tipo: entrada.tipoProducto } : {}),
  });
}

export function registrarClienteCreadoExitoso(entrada: {
  entorno: EntornoAnalitica;
  origen: OrigenCliente;
  tipoDocumento?: string;
  tieneEmail?: boolean;
}): void {
  capturarEvento(EVENTOS_ANALITICA.CLIENTE_CREADO_EXITOSO, {
    entorno: entrada.entorno,
    origen: entrada.origen,
    ...(entrada.tipoDocumento ? { tipo_documento_cliente: entrada.tipoDocumento } : {}),
    ...(typeof entrada.tieneEmail === 'boolean' ? { cliente_tiene_email: entrada.tieneEmail } : {}),
  });
}

export function registrarImportacionCompletada(entrada: {
  entorno: EntornoAnalitica;
  entidad: EntidadImportacion;
  resultado: ResultadoImportacion;
  erroresRango: ErroresRangoImportacion;
  totalFilas?: number;
  filasValidas?: number;
  filasInvalidas?: number;
}): void {
  capturarEvento(EVENTOS_ANALITICA.IMPORTACION_COMPLETADA, {
    entorno: entrada.entorno,
    entidad: entrada.entidad,
    resultado: entrada.resultado,
    errores_rango: entrada.erroresRango,
    ...(typeof entrada.totalFilas === 'number' ? { total_filas: entrada.totalFilas } : {}),
    ...(typeof entrada.filasValidas === 'number' ? { filas_validas: entrada.filasValidas } : {}),
    ...(typeof entrada.filasInvalidas === 'number' ? { filas_invalidas: entrada.filasInvalidas } : {}),
  });
}
